import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import './DocumentViewer.css';

const DocumentViewer = () => {
  const { attachmentId } = useParams();
  const navigate = useNavigate();
  const [attachment, setAttachment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    const fetchAttachment = async () => {
      try {
        // Get attachment details
        const response = await api.get(`/attachments/${attachmentId}`);
        setAttachment(response.data);

        // Fetch the file as blob
        const fileResponse = await api.get(`/attachments/${attachmentId}/download`, {
          responseType: 'blob',
          params: { preview: true }
        });

        // Create blob URL
        const url = window.URL.createObjectURL(new Blob([fileResponse.data], {
          type: response.data.content_type
        }));
        setBlobUrl(url);
        setLoading(false);
      } catch (err) {
        console.error('Error loading attachment:', err);
        setError('Failed to load document');
        setLoading(false);
      }
    };

    fetchAttachment();

    // Cleanup
    return () => {
      if (blobUrl) {
        window.URL.revokeObjectURL(blobUrl);
      }
    };
  }, [attachmentId]);

  const handleDownload = () => {
    if (blobUrl && attachment) {
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = attachment.original_filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleClose = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="document-viewer">
        <div className="viewer-header">
          <button className="btn-close" onClick={handleClose}>✕</button>
        </div>
        <div className="viewer-loading">Loading document...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="document-viewer">
        <div className="viewer-header">
          <button className="btn-close" onClick={handleClose}>✕</button>
        </div>
        <div className="viewer-error">{error}</div>
      </div>
    );
  }

  const isImage = attachment.is_image || attachment.content_type?.startsWith('image/');
  const isPdf = attachment.is_pdf || attachment.content_type === 'application/pdf';
  const isOfficeDoc = attachment.content_type && (
    attachment.content_type.includes('word') ||
    attachment.content_type.includes('excel') ||
    attachment.content_type.includes('spreadsheet') ||
    attachment.content_type.includes('powerpoint') ||
    attachment.content_type.includes('presentation')
  );

  return (
    <div className="document-viewer">
      <div className="viewer-header">
        <div className="viewer-title">
          <span className="viewer-filename">{attachment.original_filename}</span>
          <span className="viewer-filesize">
            {attachment.file_size_formatted || `${(attachment.file_size / 1024).toFixed(1)} KB`}
          </span>
        </div>
        <div className="viewer-actions">
          <button className="btn btn-sm btn-secondary" onClick={handleDownload}>
            ⬇️ Download
          </button>
          <button className="btn-close" onClick={handleClose}>✕</button>
        </div>
      </div>

      <div className="viewer-content">
        {isImage && (
          <div className="viewer-image-container">
            <img src={blobUrl} alt={attachment.original_filename} />
          </div>
        )}
        
        {isPdf && (
          <iframe
            src={blobUrl}
            title={attachment.original_filename}
            className="viewer-iframe"
          />
        )}

        {isOfficeDoc && (
          <div className="viewer-office-wrapper">
            <iframe
              src={blobUrl}
              title={attachment.original_filename}
              className="viewer-iframe"
            />
            <div className="office-download-hint">
              <p>
                If the document doesn't display correctly, you can{' '}
                <button className="link-button" onClick={handleDownload}>
                  download it
                </button>
                {' '}to open in Microsoft Office.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentViewer;
